import { Dependencies } from './interfaces/dependencies-types';
import { CtaButtonsController } from './controllers/cta-buttons-controller';
import { ChatController } from './controllers/chat-controller';
import { LoaderController } from './controllers/loader-controller';
import { ClientCookieService } from './services/client-cookie-service';
import { ModalController } from './controllers/modal-controller';
import { AccountsController } from './controllers/accounts-controller';
import { IframeController } from './controllers/iframe-controller';
import { ObfClientSettings } from './interfaces/obf-client-settings.interface';
import { EmbedController } from './controllers/embed-controller';
import { OptionsService } from './services/options-service';
import DependenciesInjector from './helpers/dependencies-injector';
import { dispatchCustomEvent, sendReport, getFormattedMailReport } from './helpers/helpers';
// import { Interaction } from './modules/tracking/controllers/interaction-controller';


declare global {
    interface Window {
        obfSdk: any;
        obfOptions: any;
        dataLayer: any;
        obfDevelopmentSettings?: any;
        ActiveXObject: any;
        hj: any;
        obfClientSDK: any;
        fcWidget: any;
        sosint: any;
    }
}

export class ClientSDKModule implements Dependencies {
    constructor() {
        this.initialize();
    }

    async initialize() {
        DependenciesInjector.register([
            { key: 'XRMSDKModule', construct: () => { return import('./modules/xrmsdk/xrmsdk.module'); } },
            { key: 'SearchModule', construct: () => { return import('./modules/search/search.module'); } },
            { key: 'ReactSearchModule', construct: () => { return import('./modules/react-search/react-search.module'); } },
            { key: 'OptionsService', construct: OptionsService },
            { key: 'ClientCookieService', construct: ClientCookieService },
            { key: 'ModalController', construct: ModalController },
            { key: 'LoaderController', construct: LoaderController },
            { key: 'ChatController', construct: ChatController },
            // { key: 'Interaction', construct: Interaction },
            { key: 'CtaButtonsController', construct: CtaButtonsController },
            { key: 'AccountsController', construct: AccountsController }
        ], this);
    }

    // tslint:disable-next-line:ban-types
    public autoInitClient(callback: Function) {
        if (document.readyState !== 'complete') {
            const loadHandler = () => {
                if (document.readyState === 'complete') {
                    this.init(callback);
                    window.removeEventListener('load', loadHandler);
                }
            }
            window.addEventListener('load', loadHandler, false);
        } else {
            this.init(callback);
        }
    }


    // tslint:disable-next-line:ban-types
    private async init(callback: Function) {

        //** register OptionsService  */
        const optionsService: OptionsService = await DependenciesInjector.getRegistered('OptionsService') as OptionsService;

        if (optionsService.checkForObfOptions()) {

            if (optionsService.validateObfOptions()) {

                const obfClientSettings = optionsService.getObfClientSettings();

                // Login user handler for the accounts
                if (
                    obfClientSettings.accounts_url &&
                    !obfClientSettings.accounts_url.includes(document.location.origin)) {
                    // Trigger initialize 
                    DependenciesInjector.getRegistered('AccountsController');
                }

                const type = checkType(obfClientSettings);

                switch (type) {
                    case 'EmbedController':
                        DependenciesInjector.register([
                            { key: 'ObfControllerInstance', construct: EmbedController }], this);
                        break;

                    case 'IframeController':
                        DependenciesInjector.register([
                            { key: 'ObfControllerInstance', construct: IframeController }], this);
                        break;
                }

                let obf: EmbedController | IframeController = await DependenciesInjector.getRegistered('ObfControllerInstance');


                callback((new ObfClientSideSDK(obf)));

                if (obfClientSettings.search) {
                    // ! temp until is tested.
                    const searchInit = async () => {
                        const options: any = await (obf as IframeController).requestConfiguration();
                        if (options.reactSearch) {
                            try {
                                await DependenciesInjector.getRegistered('ReactSearchModule');
                            } catch (e) {
                                try {
                                    await DependenciesInjector.getRegistered('SearchModule');
                                } catch (e) {
                                    dispatchCustomEvent('book-search', {
                                        status: 'error',
                                        message: 'Fail to load module'
                                    })
                                }
                            }
                        } else {
                            try {
                                await DependenciesInjector.getRegistered('SearchModule');
                            } catch (e) {
                                dispatchCustomEvent('book-search', {
                                    status: 'error',
                                    message: 'Fail to load module'
                                })
                            }
                        }

                    }

                    const config = (obf as IframeController).getXrmSdkConfig();

                    if (!config) {
                        const obfInitListener = async () => {
                            await searchInit();
                            document.removeEventListener(
                                'obf-client-loaded',
                                obfInitListener
                            );
                        };
                        document.addEventListener('obf-client-loaded', obfInitListener);
                    } else {
                        await searchInit();
                    }
                }

            } else {
                console.error('No found minimal obfOptions');

                // send mail report for [obf fialed to open] / timeout is cleared on obf open
                const reportData = getFormattedMailReport('invalid obfOptions');
                sendReport(reportData);

                failInitSDK();
                callback(null);
            }
        } else {
            // tslint:disable-next-line:no-console
            console.error('No found obfOptions');

            // send mail report for [obf fialed to open] / timeout is cleared on obf open
            const reportData = getFormattedMailReport('obfOptions not found');
            sendReport(reportData);

            failInitSDK();
            callback(null);
        }


        function checkType(settings: ObfClientSettings) {
            const element = (settings.obfElement = document.querySelector('[obfContainer]'));

            if (element) {
                return 'EmbedController';
            } else {
                return 'IframeController';
            }
        }

        function failInitSDK() {
            document.body.addEventListener('click', (e: any) => {
                const target = e.target;

                if (target.getAttribute('data-book-btn') || target.getAttribute('data-free-quote-btn')) {
                    e.preventDefault();
                    const redirectTo = window.location.origin + '/';
                    window.location.replace(redirectTo + 'contact-us');
                }
            });
        }

        return;
    }
}

class ObfClientSideSDK {
    public obfInstanceFunctions: { [key: string]: any } = {};

    public sdkInit = () => {
        return new Promise(async (res, rej) => {
            const xrmSdkConfig = (await DependenciesInjector.getRegistered('ObfControllerInstance')).getXrmSdkConfig();

            if (!xrmSdkConfig) {
                rej('Fail obf still don\'t loaded please try again when success init obf');
            };

            DependenciesInjector.getRegistered('XRMSDKModule').then(module => {
                res('success');
            }).catch(error => {
                rej(error);
            });
        })
    }

    public sdkGetCategories = async (...args: any) => {
        return (await DependenciesInjector.getRegistered('XRMSDKController')).ServicesDataService.getCategories(...args);
    }

    public sdkGetServices = async (...args: any) => {
        return (await DependenciesInjector.getRegistered('XRMSDKController')).ServicesDataService.getServices(...args);
    }

    public sdkGetService = async (...args: any) => {
        return (await DependenciesInjector.getRegistered('XRMSDKController')).ServicesDataService.getService(...args);
    }

    constructor(obfInstance: any) {
        if (obfInstance instanceof IframeController) {
            this.obfInstanceFunctions.type = 'iframe';
            this.obfInstanceFunctions.isInit = () => {
                return obfInstance.isInit();
            };
            this.obfInstanceFunctions.openObf = () => {
                return obfInstance.openObf();
            };
            this.obfInstanceFunctions.isOpenObf = () => {
                return obfInstance.isOpen();
            };
            this.obfInstanceFunctions.loadObf = () => {
                return obfInstance.loadObf();
            };

        }

        if (obfInstance instanceof EmbedController) {
            // TODO: need to be build 
        }
    }
}
